/**
 * Phase 17 — the container's gateways as today's checks judged them: each
 * lane (Phase 40 — "23Telecom · AUSSIEWIN"; a row judged on the gateway's
 * own report names the gateway) blocked for the day with the rule that
 * matched, or one quiet line when they were judged and none was blocked.
 * Nothing when the container has no gateway rule (no rows in the report) —
 * unless another container blocked a gateway for the whole country today
 * (14/10/2026), which every container's card names.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Ban } from 'lucide-react';
import type { DropGenGatewayVerdict } from '@trafficpulse/shared';

import { laneOrRouteName, laneRowKey } from '../../../utils/lanes/lane-words';

/** The container's own rows, plus every gateway another container blocked for the whole country. */
export function gatewaysFor(all: DropGenGatewayVerdict[] | undefined, containerId: string): DropGenGatewayVerdict[] | undefined {
  if (!all) return undefined;
  const out = all.filter((g) => g.containerId === containerId);
  const seen = new Set(out.filter((g) => g.blockScope === 'COUNTRY').map((g) => g.routeId));
  for (const g of all) {
    if (g.containerId === containerId || g.blockScope !== 'COUNTRY' || g.action === 'none' || seen.has(g.routeId)) continue;
    seen.add(g.routeId);
    out.push(g);
  }
  return out;
}

export const GatewayBlocksLine: FC<{ gateways?: DropGenGatewayVerdict[] }> = ({ gateways }) => {
  if (!gateways || gateways.length === 0) return null;
  const blocked = gateways.filter((g) => g.action !== 'none');
  const noun = gateways.some((g) => g.laneId !== '*') ? 'lane' : 'gateway';
  if (blocked.length === 0)
    return (
      <p className="text-xs text-muted-foreground">
        {gateways.length} {noun}{gateways.length === 1 ? '' : 's'} judged today · none blocked
      </p>
    );
  return (
    <ul className="flex flex-col gap-1 text-xs">
      {blocked.map((g) => (
        <li key={laneRowKey(g)} className="flex items-start gap-1.5 text-destructive">
          <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {g.blockScope === 'COUNTRY' ? (
            <span>
              <b>{g.routeName ?? `route ${g.routeId}`}</b> blocked for today in every container — {g.reason}
              <span className="text-muted-foreground"> · judged in {g.containerName}</span>
            </span>
          ) : (
            <span>
              <b>{laneOrRouteName(g)}</b> blocked for today — {g.reason}
              <span className="text-muted-foreground"> · its unsent drafts moved to the other gateways</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
};
